import cuid from "cuid";

export const CONDITION_OP_VALUES = {
  IDENTITY: "identity",
  NOT_IDENTITY: "not_identity",
  GT: "gt",
  GTE: "gte",
  LT: "lt",
  LTE: "lte",
  INCLUDES: "includes",
  IS_EMPTY: "isEmpty",
  NOT_EMPTY: "not_empty",
};

export const DEFAULT_CONDITION_OPERATION = CONDITION_OP_VALUES.IDENTITY;

export const DEFAULT_STATE_PREVIEW_KEY = "default";

/**
 * One line naming what makes a state appear. It labels the chips, and inside a
 * proposal it is the only thing that says which field and value the model
 * settled on: the markup can be read on screen, the condition cannot.
 */
export const conditionSummary = (conditions = []) =>
  (conditions || [])
    .map((condition) => {
      const accessor = condition?.leftOperand?.accessor;
      if (accessor === undefined || accessor === null) return null;

      // The symbol is read from the operations table rather than written in:
      // a state that fires on "greater than" used to be labelled "=", which
      // describes a different state than the one that will actually trigger.
      const operation =
        normalizeOperation(condition?.operation) || DEFAULT_CONDITION_OPERATION;
      const symbol = operationSymbol(operation);

      // Emptiness tests carry no right-hand side worth showing: their stored
      // value is the test itself, and printing it reads as a comparison.
      if (
        operation === CONDITION_OP_VALUES.IS_EMPTY ||
        operation === CONDITION_OP_VALUES.NOT_EMPTY
      ) {
        return `${accessor} ${symbol}`;
      }

      const value = condition?.rightOperand?.value;
      return `${accessor} ${symbol} ${
        value === undefined || value === null ? "?" : value
      }`;
    })
    .filter(Boolean)
    .join(" · ");

export const CONDITION_OPERATIONS = [
  {
    value: CONDITION_OP_VALUES.IDENTITY,
    labelKey: "EQUALS",
    valueType: "text",
    symbol: "=",
  },
  {
    value: CONDITION_OP_VALUES.NOT_IDENTITY,
    labelKey: "NOT_EQUALS",
    valueType: "text",
    symbol: "≠",
  },
  {
    value: CONDITION_OP_VALUES.GT,
    labelKey: "GREATER_THAN",
    valueType: "number",
    symbol: ">",
  },
  {
    value: CONDITION_OP_VALUES.GTE,
    labelKey: "GREATER_OR_EQUAL",
    valueType: "number",
    symbol: "≥",
  },
  {
    value: CONDITION_OP_VALUES.LT,
    labelKey: "LESS_THAN",
    valueType: "number",
    symbol: "<",
  },
  {
    value: CONDITION_OP_VALUES.LTE,
    labelKey: "LESS_OR_EQUAL",
    valueType: "number",
    symbol: "≤",
  },
  {
    value: CONDITION_OP_VALUES.INCLUDES,
    labelKey: "INCLUDES",
    valueType: "text",
    symbol: "⊃",
  },
  {
    value: CONDITION_OP_VALUES.IS_EMPTY,
    labelKey: "IS_EMPTY",
    valueType: "boolean",
    symbol: "∅",
  },
  {
    value: CONDITION_OP_VALUES.NOT_EMPTY,
    labelKey: "NOT_EMPTY",
    valueType: "none",
    symbol: "≠∅",
  },
];

const OPERATION_ALIASES = {
  "=": CONDITION_OP_VALUES.IDENTITY,
  equals: CONDITION_OP_VALUES.IDENTITY,
  eq: CONDITION_OP_VALUES.IDENTITY,
  "!=": CONDITION_OP_VALUES.NOT_IDENTITY,
  not_equals: CONDITION_OP_VALUES.NOT_IDENTITY,
  neq: CONDITION_OP_VALUES.NOT_IDENTITY,
  ">": CONDITION_OP_VALUES.GT,
  greater_than: CONDITION_OP_VALUES.GT,
  ">=": CONDITION_OP_VALUES.GTE,
  "≥": CONDITION_OP_VALUES.GTE,
  greater_or_equal: CONDITION_OP_VALUES.GTE,
  "<": CONDITION_OP_VALUES.LT,
  less_than: CONDITION_OP_VALUES.LT,
  "<=": CONDITION_OP_VALUES.LTE,
  "≤": CONDITION_OP_VALUES.LTE,
  less_or_equal: CONDITION_OP_VALUES.LTE,
  contains: CONDITION_OP_VALUES.INCLUDES,
  empty: CONDITION_OP_VALUES.IS_EMPTY,
  is_empty: CONDITION_OP_VALUES.IS_EMPTY,
  isempty: CONDITION_OP_VALUES.IS_EMPTY,
  not_empty: CONDITION_OP_VALUES.NOT_EMPTY,
  nonempty: CONDITION_OP_VALUES.NOT_EMPTY,
};

export const normalizeOperation = (operation) => {
  if (!operation) return operation;
  const key = String(operation).trim().toLowerCase();
  if (OPERATION_ALIASES[key]) return OPERATION_ALIASES[key];
  return operation;
};

export const operationByValue = (value) =>
  CONDITION_OPERATIONS.find((operation) => operation.value === value) || null;

export const operationSymbol = (value) => {
  const operation = operationByValue(value);
  return operation ? operation.symbol : value;
};

export const applyConditionOperation = (condition, nextOperation) => {
  const nextMeta = operationByValue(nextOperation) || {};
  const prevMeta = operationByValue(condition.operation) || {};
  const rightOperand = { ...(condition.rightOperand || {}) };
  if (nextOperation === CONDITION_OP_VALUES.IS_EMPTY) {
    rightOperand.value = true;
  } else if (nextOperation === CONDITION_OP_VALUES.NOT_EMPTY) {
    delete rightOperand.value;
  } else if (
    prevMeta.valueType === "boolean" &&
    nextMeta.valueType !== "boolean" &&
    typeof rightOperand.value === "boolean"
  ) {
    delete rightOperand.value;
  }
  return {
    ...condition,
    operation: nextOperation,
    rightOperand,
  };
};

const humanizeSegment = (segment) =>
  segment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (char) => char.toUpperCase());

export const humanizeAccessor = (accessor) =>
  accessor.split(".").map(humanizeSegment).join(" · ");

const CUSTOM_ENTITY_KEY = "custom";

const variableAccessor = (entryKey, field) => {
  if (entryKey === CUSTOM_ENTITY_KEY)
    return `${CUSTOM_ENTITY_KEY}.${field.key}`;
  const target = field.target;
  if (!target || !target.property) return `${entryKey}.${field.key}`;
  if (target.entity && target.entity !== "project")
    return [target.entity, target.property, target.filterValue]
      .filter(Boolean)
      .join(".");
  return target.property;
};

const cloneRulesWithIds = (rules) =>
  (Array.isArray(rules) ? rules : []).map((rule) => ({
    ...rule,
    id: cuid(),
  }));

export const buildFormFieldFromVariable = (variable) => ({
  key: variable.accessor,
  accessor: variable.accessor,
  label: variable.label,
  required: !!variable.required,
  display: false,
  options: variable.options || null,
  type: variable.type || null,
  rules: cloneRulesWithIds(variable.defaultRules),
  inputConfig: variable.inputConfig || {},
  visibleWhen: [],
});

export const applyVariableDefaults = (field, variable) => ({
  ...field,
  key: variable.accessor,
  accessor: variable.accessor,
  label: variable.label,
  options: variable.options || null,
  required: !!variable.required,
  type: variable.type || null,
  rules: cloneRulesWithIds(variable.defaultRules),
  inputConfig: variable.inputConfig || {},
});

export const fieldRegistryToVariables = (registry = []) =>
  registry.flatMap((entry) =>
    (entry.fields || []).map((field) => ({
      accessor: variableAccessor(entry.key, field),
      // Registry name, as opposed to the runtime accessor above: the API
      // rebuilds accessors itself and expects to be given a field name.
      field: `${entry.key}.${field.key}`,
      label: field.label || humanizeAccessor(`${entry.key}.${field.key}`),
      options: field.options || null,
      type: field.type || null,
      required: !!field.required,
      defaultRules: field.defaultRules || null,
      inputConfig: field.inputConfig || null,
    })),
  );
